import { useState, useEffect } from 'react';
import { STORAGE_KEYS, DEFAULTS } from '../constants';

/**
 * Custom hook for managing filter state with localStorage persistence
 * @param {Object} options - Configuration options
 * @param {string} options.pageKey - Unique key for this page's filters (e.g., 'dashboard', 'analytics')
 * @param {string} [options.defaultPeriod] - Default period value
 * @param {string|null} [options.defaultPlatform] - Default platform value
 * @param {string|null} [options.defaultStatus] - Default status value
 * @returns {Object} - Filter state and setters
 */
export const useFilterPersistence = ({
  pageKey,
  defaultPeriod = DEFAULTS.PERIOD,
  defaultPlatform = DEFAULTS.PLATFORM,
  defaultStatus = DEFAULTS.STATUS
} = {}) => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [platform, setPlatform] = useState(defaultPlatform);
  const [status, setStatus] = useState(defaultStatus);

  // Load saved preferences on mount
  useEffect(() => {
    if (!pageKey) return;

    const periodKey = `${STORAGE_KEYS.FILTER_PERIOD}-${pageKey}`;
    const platformKey = `${STORAGE_KEYS.FILTER_PLATFORM}-${pageKey}`;
    const statusKey = `${STORAGE_KEYS.FILTER_STATUS}-${pageKey}`;

    const savedPeriod = localStorage.getItem(periodKey);
    const savedPlatform = localStorage.getItem(platformKey);
    const savedStatus = localStorage.getItem(statusKey);

    if (savedPeriod) setPeriod(savedPeriod);
    // Handle platform: if 'null' string, set to null; otherwise use saved value or keep default
    if (savedPlatform !== null) {
      setPlatform(savedPlatform === 'null' ? null : savedPlatform);
    }
    // Handle status: if 'null' string, set to null; otherwise use saved value or keep default
    if (savedStatus !== null) {
      setStatus(savedStatus === 'null' ? null : savedStatus);
    }
  }, [pageKey]);

  // Save preferences when they change
  useEffect(() => {
    if (!pageKey) return;

    const periodKey = `${STORAGE_KEYS.FILTER_PERIOD}-${pageKey}`;
    const platformKey = `${STORAGE_KEYS.FILTER_PLATFORM}-${pageKey}`;
    const statusKey = `${STORAGE_KEYS.FILTER_STATUS}-${pageKey}`;

    localStorage.setItem(periodKey, period);
    localStorage.setItem(platformKey, platform || 'null');
    localStorage.setItem(statusKey, status || 'null');
  }, [pageKey, period, platform, status]);

  return {
    period,
    setPeriod,
    platform,
    setPlatform,
    status,
    setStatus
  };
};

